import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateJob, useSkills } from "@/lib/api/hooks";
import type { JobPriority } from "@/lib/mock/types";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/new")({
  component: NewJobPage,
  head: () => ({ meta: [{ title: "New job — CSaaS" }] }),
});

function NewJobPage() {
  const navigate = useNavigate();
  const { data: skills = [], isLoading: skillsLoading } = useSkills();
  const createJob = useCreateJob();

  const defaultSkill = useMemo(() => skills[0]?.name ?? "", [skills]);
  const [skillName, setSkillName] = useState<string>("");
  const [prompt, setPrompt] = useState("");
  const [contextStr, setContextStr] = useState("");
  const [priority, setPriority] = useState<JobPriority>("normal");
  const [timeout, setTimeoutSec] = useState(300);
  const [contextError, setContextError] = useState<string | null>(null);

  const effectiveSkill = skillName || defaultSkill;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveSkill) {
      toast.error("Pick a skill before submitting.");
      return;
    }
    let context: Record<string, unknown> | null = null;
    if (contextStr.trim()) {
      try {
        context = JSON.parse(contextStr);
      } catch {
        setContextError("Context must be valid JSON.");
        return;
      }
    }
    setContextError(null);
    createJob.mutate(
      { skillName: effectiveSkill, prompt, priority, timeoutSeconds: timeout, context },
      {
        onSuccess: (job) => {
          toast.success("Job queued");
          navigate({ to: "/jobs/$id", params: { id: job.id } });
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">New job</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Submit a skill execution to the worker pool.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Job parameters</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label>Skill</Label>
              <Select
                value={effectiveSkill}
                onValueChange={setSkillName}
                disabled={skillsLoading || skills.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder={skillsLoading ? "Loading skills…" : "Select a skill"} />
                </SelectTrigger>
                <SelectContent>
                  {skills.map((s) => (
                    <SelectItem key={s.name} value={s.name}>
                      <span className="font-medium">{s.name}</span>
                      {s.description && (
                        <span className="text-muted-foreground ml-2 text-xs">{s.description}</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="prompt">Prompt</Label>
              <Textarea
                id="prompt"
                rows={5}
                maxLength={4000}
                placeholder="Describe what the skill should do…"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
              <div className="text-[11px] text-muted-foreground text-right">
                {prompt.length} / 4000
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ctx">Context (optional JSON)</Label>
              <Textarea
                id="ctx"
                rows={4}
                placeholder='{"repo": "acme/web", "branch": "main"}'
                value={contextStr}
                onChange={(e) => setContextStr(e.target.value)}
                className="font-mono text-xs"
              />
              {contextError && <p className="text-xs text-destructive">{contextError}</p>}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={priority} onValueChange={(v) => setPriority(v as JobPriority)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Timeout · {timeout}s</Label>
                <Slider
                  min={30}
                  max={900}
                  step={30}
                  value={[timeout]}
                  onValueChange={([v]) => setTimeoutSec(v)}
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={!prompt.trim() || createJob.isPending || !effectiveSkill}
            >
              {createJob.isPending ? "Submitting…" : "Submit job"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
